import type { PolicyAssignment, PolicyDryRun, RuntimePolicy, RuntimePolicyBody } from '@ralph/contracts'
import { Copy, Plus, Trash2 } from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import { isPolicyInUse, isPolicyVersionConflict, type UpsertPolicyInput } from '@/api/policies.ts'
import { Notice, riseDelay, SectionHead, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Input, Panel, Select, Textarea } from '@/components/ui/primitives.tsx'
import {
  useAssignPolicy,
  useCreatePolicy,
  useDeletePolicy,
  useDryRunPolicy,
  usePolicies,
  usePolicyAssignments,
  useUpdatePolicy
} from '@/hooks/use-policies.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'
import { MODEL_ALIASES, RETRIEVAL_STRATEGIES } from './policy-options.ts'

/**
 * Runtime profiles and their assignments (AGM-100…106).
 *
 * The panel is built around one rule: nothing here is edited without seeing who it reaches.
 * A profile is not a per-agent setting — it is a shared object every referencing agent
 * resolves through on its next run — so the list leads with the assigned count, the editor
 * refuses to save an existing profile until its dry run has been read, and a delete that the
 * server refuses is rendered as the list of agents to reassign rather than as a conflict
 * code (AGM-105).
 *
 * The form is deliberately complete and technical. A profile is not layered and not partial:
 * "which numbers does this agent run under" must have one readable answer, so every field is
 * present on every profile, and a half-filled form is not a thing that can exist. That is
 * also why nothing here is set in the display face: this is an instrument panel, and every
 * value on it is a number that some run will be measured against.
 */
export const PoliciesPanel = ({ enabled }: { enabled: boolean }) => {
  const t = useT().admin.policies
  const policies = usePolicies(enabled)
  const assignments = usePolicyAssignments(enabled)
  const [editing, setEditing] = useState<{ policy: RuntimePolicy | null; seed: RuntimePolicyBody } | null>(null)

  const items = policies.data?.items ?? []
  // A new profile starts from the built-in one rather than from invented numbers: those
  // values came from this deployment's environment, and a "new profile" that silently
  // disagreed with them would be a policy change disguised as a create.
  const seedBody = (items.find((policy) => policy.builtin) ?? items[0])?.body ?? FALLBACK_BODY

  return (
    <div className="space-y-7">
      <section>
        <SectionHead
          title={t.title}
          lead={t.lead}
          action={
            <Button
              size="sm"
              icon={<Plus size={12} />}
              disabled={policies.isError}
              onClick={() => setEditing({ policy: null, seed: seedBody })}
            >
              {t.newProfile}
            </Button>
          }
        />

        {policies.isLoading && <p className="mt-3 text-[13px] text-fg-subtle">{t.loading}</p>}

        {policies.isError && (
          <div className="mt-3">
            <EmptyState title={t.unavailable} body={t.unavailableBody} />
          </div>
        )}

        {!policies.isLoading && !policies.isError && items.length === 0 && (
          <div className="mt-3">
            <EmptyState title={t.empty} body={t.emptyBody} />
          </div>
        )}

        <ul className="mt-4 space-y-2.5">
          {items.map((policy, index) => (
            <li key={policy.id}>
              <PolicyRow
                policy={policy}
                index={index}
                assignments={assignments.data?.items ?? []}
                onEdit={() => setEditing({ policy, seed: policy.body })}
                onDuplicate={() => setEditing({ policy: null, seed: policy.body })}
              />
            </li>
          ))}
        </ul>
      </section>

      {editing && (
        <PolicyForm
          key={editing.policy?.id ?? 'new'}
          policy={editing.policy}
          seed={editing.seed}
          onDone={() => setEditing(null)}
        />
      )}

      <AssignmentsTable
        policies={items}
        assignments={assignments.data?.items ?? []}
        loading={assignments.isLoading}
        failed={assignments.isError}
      />
    </div>
  )
}

const PolicyRow = ({
  policy,
  index,
  assignments,
  onEdit,
  onDuplicate
}: {
  policy: RuntimePolicy
  index: number
  assignments: PolicyAssignment[]
  onEdit: () => void
  onDuplicate: () => void
}) => {
  // In the component body, not at module scope — a captured formatter, or a captured
  // catalogue branch, keeps whichever locale was active when the module first evaluated and
  // then renders it forever with nothing failing (PRD-27 F-3, LOC-012).
  const fmt = useI18n()
  const t = fmt.t.admin.policies
  const remove = useDeletePolicy()
  const referencing = assignments.filter((assignment) => assignment.policyId === policy.id)
  const refused = remove.isError && isPolicyInUse(remove.error)

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2.5">
        <div className="min-w-0 flex-1 basis-[15rem]">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <h3 className="min-w-0 truncate text-[14px]">{policy.name}</h3>
            <span className="readout text-fg-subtle">{t.version(policy.version)}</span>
            {policy.builtin && <Badge>{t.builtin}</Badge>}
            <Badge tone={policy.assignedAgents > 0 ? 'primary' : 'neutral'}>
              {t.assigned(fmt, policy.assignedAgents)}
            </Badge>
          </div>
          {policy.description && <p className="mt-1 text-[13px] leading-snug text-fg-muted">{policy.description}</p>}
          {/* One catalogue entry rather than five fragments, a bare `$` and two raw numbers
              assembled here: a sentence built at a call site has no Portuguese rendering, and
              an unformatted currency has no Brazilian one (PRD-27 LOC-013, LOC-020). */}
          <p className="readout mt-1.5 text-fg-subtle">
            {t.rowSummary(fmt, {
              modelAlias: policy.body.modelAlias,
              strategy: policy.body.retrieval.strategy,
              topK: policy.body.retrieval.topK,
              maxSteps: policy.body.budget.maxSteps,
              maxCostUsd: policy.body.budget.maxCostUsd
            })}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <Button size="sm" onClick={onEdit}>
            {t.edit}
          </Button>
          <Button size="sm" icon={<Copy size={12} />} onClick={onDuplicate}>
            {t.duplicate}
          </Button>
          <Button
            size="sm"
            variant="danger"
            icon={<Trash2 size={12} />}
            disabled={policy.builtin || remove.isPending}
            title={policy.builtin ? t.deleteBuiltin : undefined}
            onClick={() => remove.mutate(policy.id)}
          >
            {remove.isPending ? t.deleting : t.delete}
          </Button>
        </div>
      </div>

      {remove.isError && (
        <div className="mt-2.5">
          <Notice role="alert" title={refused ? t.deleteRefused : t.deleteFailed}>
            <p>{refused ? t.deleteRefusedBody : describeApiError(fmt.t, remove.error)}</p>
            {refused && referencing.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {referencing.map((assignment) => (
                  <li key={assignment.agentId} className="readout text-fg-muted">
                    {assignment.displayName} · {assignment.agentId}
                  </li>
                ))}
              </ul>
            )}
          </Notice>
        </div>
      )}
    </StudioRow>
  )
}

/**
 * The editor.
 *
 * `preview` is cleared by every keystroke on an existing profile, and Save is gated on it
 * being present: the preview is only meaningful for the exact draft it was produced from,
 * and a stale one would tell an administrator that nothing changes while they save something
 * that does. A brand-new profile has nothing to preview — no agent references it yet — so it
 * saves directly.
 */
const PolicyForm = ({
  policy,
  seed,
  onDone
}: {
  policy: RuntimePolicy | null
  seed: RuntimePolicyBody
  onDone: () => void
}) => {
  const messages = useT()
  const t = messages.admin.policies
  const fieldId = useId()
  const [name, setName] = useState(policy?.name ?? '')
  const [description, setDescription] = useState(policy?.description ?? '')
  const [note, setNote] = useState('')
  const [body, setBody] = useState<RuntimePolicyBody>(seed)
  const [preview, setPreview] = useState<PolicyDryRun | null>(null)

  const create = useCreatePolicy()
  const update = useUpdatePolicy()
  const dryRun = useDryRunPolicy()

  const input: UpsertPolicyInput = { name: name.trim(), description: description.trim(), body, note: note.trim() }
  const busy = create.isPending || update.isPending
  const conflict = update.isError && isPolicyVersionConflict(update.error)
  const canSave = name.trim() !== '' && !busy && (policy === null || preview !== null)

  /** Any edit invalidates the preview — see the note on this component. */
  const change = (next: RuntimePolicyBody) => {
    setBody(next)
    setPreview(null)
  }

  const save = () => {
    if (policy === null) create.mutate(input, { onSuccess: onDone })
    else update.mutate({ policyId: policy.id, version: policy.version, input }, { onSuccess: onDone })
  }

  return (
    <Panel solid className="fade-in px-4 py-4">
      <h3 className="text-[14.5px]">{policy === null ? t.newProfile : policy.name}</h3>

      <div className="mt-3.5 grid gap-3 sm:grid-cols-2">
        <Field id={`${fieldId}-name`} label={t.nameLabel}>
          <Input
            id={`${fieldId}-name`}
            value={name}
            placeholder={t.namePlaceholder}
            disabled={policy?.builtin}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field id={`${fieldId}-description`} label={t.descriptionLabel}>
          <Input
            id={`${fieldId}-description`}
            value={description}
            placeholder={t.descriptionPlaceholder}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>

      <Section title={t.sections.model}>
        <Field id={`${fieldId}-model`} label={t.fields.modelAlias}>
          <EnumSelect
            id={`${fieldId}-model`}
            value={body.modelAlias}
            options={MODEL_ALIASES}
            onChange={(value) => change({ ...body, modelAlias: value as RuntimePolicyBody['modelAlias'] })}
          />
        </Field>
      </Section>

      <Section title={t.sections.retrieval}>
        <Field id={`${fieldId}-strategy`} label={t.fields.strategy}>
          <EnumSelect
            id={`${fieldId}-strategy`}
            value={body.retrieval.strategy}
            options={RETRIEVAL_STRATEGIES}
            onChange={(value) =>
              change({
                ...body,
                retrieval: { ...body.retrieval, strategy: value as RuntimePolicyBody['retrieval']['strategy'] }
              })
            }
          />
        </Field>
        <NumberField
          id={`${fieldId}-topk`}
          label={t.fields.topK}
          value={body.retrieval.topK}
          min={1}
          max={50}
          onChange={(topK) => change({ ...body, retrieval: { ...body.retrieval, topK } })}
        />
        <NumberField
          id={`${fieldId}-minscore`}
          label={t.fields.minScore}
          value={body.retrieval.minScore}
          min={0}
          step={0.01}
          onChange={(minScore) => change({ ...body, retrieval: { ...body.retrieval, minScore } })}
        />
        <NumberField
          id={`${fieldId}-candidates`}
          label={t.fields.candidates}
          value={body.retrieval.candidates}
          min={10}
          max={200}
          onChange={(candidates) => change({ ...body, retrieval: { ...body.retrieval, candidates } })}
        />
      </Section>

      <Section title={t.sections.budget}>
        <NumberField
          id={`${fieldId}-steps`}
          label={t.fields.maxSteps}
          value={body.budget.maxSteps}
          min={1}
          onChange={(maxSteps) => change({ ...body, budget: { ...body.budget, maxSteps } })}
        />
        <NumberField
          id={`${fieldId}-modelcalls`}
          label={t.fields.maxModelCalls}
          value={body.budget.maxModelCalls}
          min={1}
          onChange={(maxModelCalls) => change({ ...body, budget: { ...body.budget, maxModelCalls } })}
        />
        <NumberField
          id={`${fieldId}-toolcalls`}
          label={t.fields.maxToolCalls}
          value={body.budget.maxToolCalls}
          min={1}
          onChange={(maxToolCalls) => change({ ...body, budget: { ...body.budget, maxToolCalls } })}
        />
        <NumberField
          id={`${fieldId}-delegations`}
          label={t.fields.maxDelegations}
          value={body.budget.maxDelegations}
          min={0}
          onChange={(maxDelegations) => change({ ...body, budget: { ...body.budget, maxDelegations } })}
        />
        <NumberField
          id={`${fieldId}-cost`}
          label={t.fields.maxCostUsd}
          value={body.budget.maxCostUsd}
          min={0}
          step={0.05}
          onChange={(maxCostUsd) => change({ ...body, budget: { ...body.budget, maxCostUsd } })}
        />
        <NumberField
          id={`${fieldId}-wallclock`}
          label={t.fields.maxWallClockMs}
          value={body.budget.maxWallClockMs}
          min={1000}
          step={1000}
          onChange={(maxWallClockMs) => change({ ...body, budget: { ...body.budget, maxWallClockMs } })}
        />
      </Section>

      <Section title={t.sections.approval}>
        <NumberField
          id={`${fieldId}-ttl`}
          label={t.fields.ttlMs}
          value={body.approval.ttlMs}
          min={1000}
          step={1000}
          onChange={(ttlMs) => change({ ...body, approval: { ...body.approval, ttlMs } })}
        />
        <NumberField
          id={`${fieldId}-rounds`}
          label={t.fields.maxRounds}
          value={body.approval.maxRounds}
          min={1}
          max={20}
          onChange={(maxRounds) => change({ ...body, approval: { ...body.approval, maxRounds } })}
        />
      </Section>

      <Section title={t.sections.delegation}>
        <NumberField
          id={`${fieldId}-depth`}
          label={t.fields.maxDepth}
          value={body.delegation.maxDepth}
          min={0}
          max={3}
          onChange={(maxDepth) => change({ ...body, delegation: { ...body.delegation, maxDepth } })}
        />
        <NumberField
          id={`${fieldId}-parallel`}
          label={t.fields.maxParallel}
          value={body.delegation.maxParallel}
          min={1}
          max={8}
          onChange={(maxParallel) => change({ ...body, delegation: { ...body.delegation, maxParallel } })}
        />
      </Section>

      <div className="mt-4">
        <label htmlFor={`${fieldId}-note`} className="eyebrow mb-1 block">
          {t.noteLabel}
        </label>
        <Textarea
          id={`${fieldId}-note`}
          rows={2}
          value={note}
          placeholder={t.notePlaceholder}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      {policy !== null && (
        <div className="mt-4 border-t border-rule pt-3.5">
          <Button
            size="sm"
            disabled={dryRun.isPending}
            onClick={() => dryRun.mutate({ policyId: policy.id, input }, { onSuccess: setPreview })}
          >
            {dryRun.isPending ? t.dryRunning : t.dryRun}
          </Button>
          {preview === null && !dryRun.isPending && (
            <p className="mt-2 text-[12.5px] text-fg-subtle">{t.dryRunRequired}</p>
          )}
          {dryRun.isError && (
            <p role="alert" className="mt-2 text-[12.5px] text-alert">
              {t.dryRunFailed}
            </p>
          )}
          {preview && <DryRunReport preview={preview} />}
        </div>
      )}

      {(create.isError || update.isError) && (
        <p role="alert" className="mt-2.5 text-[12.5px] leading-relaxed text-alert">
          {conflict ? t.versionConflict : t.saveFailed}{' '}
          {conflict ? '' : describeApiError(messages, create.error ?? update.error)}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" variant="primary" disabled={!canSave} onClick={save}>
          {busy ? t.saving : t.save}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onDone}>
          {t.cancel}
        </Button>
      </div>
    </Panel>
  )
}

/** The preview (AGM-106): who is affected, and what changes for each. Zero writes happened. */
const DryRunReport = ({ preview }: { preview: PolicyDryRun }) => {
  const fmt = useI18n()
  const t = fmt.t.admin.policies

  return (
    <div className="fade-in mt-3 rounded-tile border border-rule bg-fg/[0.03] px-3.5 py-3">
      <p className="text-[13px] font-semibold">{t.dryRunTitle}</p>
      {preview.agents.length === 0 ? (
        <p className="mt-1 text-[12.5px] text-fg-muted">{t.dryRunEmpty}</p>
      ) : (
        <>
          <p className="readout mt-1 text-fg-subtle">{t.dryRunAgents(fmt, preview.agents.length)}</p>
          <ul className="mt-2.5 space-y-2.5">
            {preview.agents.map((agent) => (
              <li key={agent.agentId}>
                <p className="text-[13px]">
                  {agent.displayName} <span className="readout text-fg-subtle">{agent.agentId}</span>
                </p>
                <ul className="mt-1 space-y-0.5">
                  {agent.changes.map((change) => (
                    <li key={change.field} className="readout text-fg-muted">
                      {change.field}: {t.dryRunBefore} {formatValue(change.before)} → {t.dryRunAfter}{' '}
                      {formatValue(change.after)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

const AssignmentsTable = ({
  policies,
  assignments,
  loading,
  failed
}: {
  policies: RuntimePolicy[]
  assignments: PolicyAssignment[]
  loading: boolean
  failed: boolean
}) => {
  const messages = useT()
  const t = messages.admin.policies
  const assign = useAssignPolicy()

  return (
    <section>
      <SectionHead title={t.assignmentsTitle} lead={t.assignmentsLead} />

      {loading && <p className="mt-3 text-[13px] text-fg-subtle">{t.assignmentsLoading}</p>}
      {failed && <p className="mt-3 text-[13px] text-fg-subtle">{t.assignmentsUnavailable}</p>}
      {!loading && !failed && assignments.length === 0 && (
        <p className="mt-3 text-[13px] text-fg-subtle">{t.assignmentsEmpty}</p>
      )}

      <ul className="mt-4 space-y-2">
        {assignments.map((assignment, index) => (
          <li key={assignment.agentId}>
            <StudioRow style={riseDelay(index)}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className="min-w-0 flex-1 basis-[12rem]">
                  <p className="truncate text-[13.5px]">{assignment.displayName}</p>
                  <p className="readout text-fg-subtle">{assignment.agentId}</p>
                </div>
                <label className="eyebrow" htmlFor={`assign-${assignment.agentId}`}>
                  {t.profileColumn}
                </label>
                {/* The primitive is `w-full`; the width belongs on a wrapper rather than on a
                    competing utility whose position in Tailwind's output decides the winner. */}
                <div className="w-full sm:w-56">
                  <Select
                    id={`assign-${assignment.agentId}`}
                    value={assignment.policyId}
                    disabled={assign.isPending}
                    className="h-[30px] text-[12.5px]"
                    onChange={(event) => assign.mutate({ agentId: assignment.agentId, policyId: event.target.value })}
                  >
                    {policies.map((policy) => (
                      <option key={policy.id} value={policy.id}>
                        {policy.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </StudioRow>
          </li>
        ))}
      </ul>

      {assign.isError && (
        <p role="alert" className="mt-2.5 text-[12.5px] text-alert">
          {t.reassignFailed} {describeApiError(messages, assign.error)}
        </p>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Small form pieces
// ---------------------------------------------------------------------------

/**
 * A group of fields under a mono micro-label.
 *
 * The rule that separates the groups everywhere else in the studio is missing here on
 * purpose: a `<legend>` is laid out inside its fieldset's border box, so a hairline on the
 * fieldset gets notched by the label in some browsers and crossed by it in others. Spacing
 * carries the grouping instead, and it carries it identically everywhere.
 */
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset className="mt-5">
    <legend className="eyebrow mb-2">{title}</legend>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </fieldset>
)

const Field = ({ id, label, children }: { id: string; label: string; children: ReactNode }) => (
  <div>
    <label htmlFor={id} className="eyebrow mb-1 block">
      {label}
    </label>
    {children}
  </div>
)

const EnumSelect = ({
  id,
  value,
  options,
  onChange
}: {
  id: string
  value: string
  options: readonly string[]
  onChange: (value: string) => void
}) => (
  <Select id={id} value={value} className="readout" onChange={(event) => onChange(event.target.value)}>
    {options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </Select>
)

/**
 * A number field that can be cleared while typing.
 *
 * The committed value stays a number, but the input holds its own string: a controlled
 * numeric input that rejects the empty string snaps back to the old value the moment someone
 * selects-all and types, which makes every field on this form fight the person filling it.
 */
const NumberField = ({
  id,
  label,
  value,
  min,
  max,
  step,
  onChange
}: {
  id: string
  label: string
  value: number
  min?: number
  max?: number
  step?: number
  onChange: (value: number) => void
}) => {
  const [draft, setDraft] = useState(String(value))

  return (
    <Field id={id} label={label}>
      <Input
        id={id}
        type="number"
        value={draft}
        min={min}
        max={max}
        step={step}
        className="readout"
        onChange={(event) => {
          setDraft(event.target.value)
          const parsed = Number(event.target.value)
          if (event.target.value.trim() !== '' && Number.isFinite(parsed)) onChange(parsed)
        }}
        onBlur={() => setDraft(String(value))}
      />
    </Field>
  )
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Only reached on a deployment whose profile list is empty — i.e. before the built-in
 * profile has been seeded. Every value here is a conservative floor, not a recommendation:
 * the seeded `standard` profile is the deployment's real bootstrap.
 */
const FALLBACK_BODY: RuntimePolicyBody = {
  modelAlias: 'balanced',
  retrieval: { strategy: 'hybrid', topK: 8, minScore: 0.3, candidates: 60 },
  budget: {
    maxSteps: 12,
    maxModelCalls: 8,
    maxToolCalls: 12,
    maxDelegations: 2,
    maxCostUsd: 1,
    maxWallClockMs: 120_000
  },
  approval: { ttlMs: 900_000, maxRounds: 3 },
  delegation: { maxDepth: 1, maxParallel: 2 }
}
